import React, { useMemo, useState } from 'react'
import { TestResult, HeatmapCell, TEST_CATEGORIES, FormulaTarget } from '../types'
import { cn } from '../lib/utils'
import { Info, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronRight } from 'lucide-react'
import { getFormulaTarget } from '../utils/formulaTargets'
import { convertUnits, getServingWeight } from '../utils/unitConversion'
import { getAnalyteFriendlyName, getAnalyteMetadata } from '../data/analyteDescriptions'
import { getNormalizedSuffix, shouldNormalizeAnalyte } from '../utils/nutrientNormalization'
import { getFormulaNutrient } from '../data/formulaNutrients'
import { useRegulatoryThresholds } from '../hooks/useRegulatoryThresholds'

interface ContaminantHeatmapProps {
  data: TestResult[]
  formulaTargets: FormulaTarget[]
}

interface AnalyteGroup {
  category: string
  analytes: string[]
  individualAnalytes?: string[] // For rollup categories, stores the individual analytes
  isExpanded?: boolean // For display purposes, indicates if this is an expanded group
}

type NoLimitDisplayStyle = 'gray' | 'striped' | 'dotted' | 'icon'

type SortDirection = 'asc' | 'desc' | null

// Friendly product name mapping
const PRODUCT_FRIENDLY_NAMES: Record<string, string> = {
  'Bobbie Organic Infant Formula': 'Bobbie Organic',
  'Enfamil NeuroPro Powder Infant Formula': 'Enfamil NeuroPro',
  'Holle Stage 1 Formula Organic (Cow)': 'Holle Stage 1 Organic',
  'Kendamil Organic Infant Formula Powder': 'Kendamil Organic',
  'Similac 360 Total Care Infant Formula Powder': 'Similac 360 Total Care'
}

const getProductFriendlyName = (product: string): string => {
  return PRODUCT_FRIENDLY_NAMES[product] || product
}

export const ContaminantHeatmap: React.FC<ContaminantHeatmapProps> = ({ data, formulaTargets }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedCell, setSelectedCell] = useState<HeatmapCell | null>(null)
  const [hoveredCell, setHoveredCell] = useState<HeatmapCell | null>(null)
  const [hoverPosition, setHoverPosition] = useState<{ x: number; xRight: number; y: number } | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [orientation, setOrientation] = useState<'horizontal' | 'vertical'>('vertical')
  const [selectedGuideline, setSelectedGuideline] = useState<string>('regulatory')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [noLimitStyle, setNoLimitStyle] = useState<NoLimitDisplayStyle>('icon')
  const [sortByAnalyte, setSortByAnalyte] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)
  const [expandedRollups, setExpandedRollups] = useState<Set<string>>(new Set())
  const [hoveredAnalyteInfo, setHoveredAnalyteInfo] = useState<{ analyte: string; position: { x: number; xRight: number; y: number; yAbove: number } } | null>(null)
  const [hoveredGuidelineInfo, setHoveredGuidelineInfo] = useState<{ position: { x: number; xRight: number; y: number; yAbove: number } } | null>(null)
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const dismissPopovers = React.useCallback(() => {
    setHoveredAnalyteInfo(null)
    setHoveredCell(null)
    setHoverPosition(null)
  }, [])

  React.useEffect(() => {
    window.addEventListener('scroll', dismissPopovers, true)
    return () => window.removeEventListener('scroll', dismissPopovers, true)
  }, [dismissPopovers])

  // Fetch thresholds for both regulatory and other categories
  const { data: regulatoryThresholds } = useRegulatoryThresholds('regulatory')
  const { data: otherThresholds } = useRegulatoryThresholds('other')
  
  // Debug logging
  console.log('Selected guideline:', selectedGuideline)
  console.log('Regulatory thresholds:', regulatoryThresholds?.length)
  console.log('Other thresholds:', otherThresholds?.length)

  const heatmapData = useMemo(() => {
    // Group by product and analyte
    const productAnalyteMap = new Map<string, HeatmapCell>()
    const products = new Set<string>()
    const analytes = new Set<string>()
    
    // For percentile-based coloring, track all values per analyte
    const analyteValues = new Map<string, number[]>()

    // Ensure data is an array
    const dataArray = Array.isArray(data) ? data : []
    
    dataArray.forEach(result => {
      const productName = result.product
      const key = `${productName}|${result.analyte}`
      products.add(productName)
      analytes.add(result.analyte)
      const concentration = result.concentration
      const loq = result.loq
      
      // Get threshold from API for comparison (works for both regulatory and other)
      const thresholds = selectedGuideline === 'regulatory' ? regulatoryThresholds : otherThresholds
      const threshold = thresholds?.find(t => t.analyte === result.analyte)
      const benchmark = threshold?.converted_value_ug_kg
      
      // Find if this analyte belongs to a positive scale category
      const category = TEST_CATEGORIES.find(cat => cat.analytes.includes(result.analyte))
      const positiveScale = category?.positiveScale
      
      // Track values for percentile calculation (only for Other Standards)
      // Skip positive scale categories (nutrients - higher is better)
      if (selectedGuideline === 'other' && concentration !== null && concentration > 0 && !positiveScale) {
        if (!analyteValues.has(result.analyte)) {
          analyteValues.set(result.analyte, [])
        }
        analyteValues.get(result.analyte)!.push(concentration)
      }

      let status: HeatmapCell['status'] = 'ND'
      let colorIntensity = 0

      if (positiveScale) {
        // Positive scale logic (higher is better)
        // Check if we have a formula-specific target
        const formulaTarget = getFormulaTarget(formulaTargets, productName, result.analyte)
        
        let targetValue = positiveScale.targetValue
        let valueInTargetUnits = concentration
        
        if (formulaTarget) {
          // Use formula-specific target
          targetValue = formulaTarget.targetValue
          
          // Debug logging
          console.log(`Found formula target for ${result.analyte} in ${productName}:`, {
            targetValue,
            targetUnit: formulaTarget.unit,
            concentration: concentration,
            unit: result.unit,
            loq
          })
          
          // Convert test result to target unit
          const servingWeight = getServingWeight(productName)
          const convertedValue = convertUnits(
            concentration || 0,
            result.unit,
            formulaTarget.unit,
            { servingWeightGrams: servingWeight, productName }
          )
          
          if (convertedValue !== null) {
            valueInTargetUnits = convertedValue
            if (result.unit.toLowerCase() === formulaTarget.unit.toLowerCase()) {
              console.log(`✓ Units match (${result.unit}). Value: ${concentration}`)
            } else {
              console.log(`✓ Converted ${concentration} ${result.unit} → ${convertedValue} ${formulaTarget.unit}`)
            }
          } else {
            // Conversion failed
            console.warn(`⚠️ Cannot convert ${result.unit} to ${formulaTarget.unit} for ${result.analyte}. Using original value.`)
            valueInTargetUnits = concentration
          }
        } else {
          // Debug: No formula target found
          console.log(`No formula target found for ${result.analyte} in ${productName}`)
        }
        
        // Use LOQ as value if concentration is below LOQ
        const valueForCalc = (valueInTargetUnits !== null && valueInTargetUnits > 0) 
          ? valueInTargetUnits 
          : (loq || 0)
        
        const percentage = (valueForCalc / targetValue) * 100
        
        console.log(`Percentage for ${result.analyte} in ${productName}: ${percentage.toFixed(2)}% (${valueForCalc} / ${targetValue})`)

        if (percentage >= positiveScale.thresholds.excellent) {
          status = 'ND' // Green (excellent)
          colorIntensity = 0
        } else if (percentage >= positiveScale.thresholds.good) {
          status = 'trace' // Yellow (good)
          colorIntensity = 0.3
        } else if (percentage >= positiveScale.thresholds.fair) {
          status = 'detected' // Orange (fair)
          colorIntensity = 0.6
        } else {
          status = 'high' // Red (poor)
          colorIntensity = 1.0
        }
      } else {
        // Negative scale logic (lower is better)
        if (concentration === null || concentration === 0) {
          status = 'ND'
          colorIntensity = 0
        } else if (loq && concentration < loq) {
          status = 'trace'
          colorIntensity = 0.3
        } else if (selectedGuideline === 'other') {
          // For Other Standards, use percentile-based coloring
          // Will be calculated after all data is collected
          status = 'detected'
          colorIntensity = 0.5 // Placeholder, will be recalculated
        } else if (benchmark) {
          // For specific regulatory guidelines, use benchmark thresholds
          if (concentration < benchmark * 0.5) {
            status = 'detected'
            colorIntensity = 0.5
          } else if (concentration < benchmark) {
            status = 'detected'
            colorIntensity = 0.7
          } else {
            status = 'high'
            colorIntensity = 1.0
          }
        } else {
          // No benchmark available
          status = 'detected'
          colorIntensity = 0.6
        }
      }

      // Use converted value if available, otherwise use original
      const convertedValue = result.converted_concentration_ug_kg
      const displayConcentration = convertedValue !== null && convertedValue !== undefined ? convertedValue : concentration

      // Determine display unit
      let displayUnit = result.unit
      if (convertedValue !== null && convertedValue !== undefined) {
        // If we have a converted value, use µg/kg
        displayUnit = 'µg/kg'
      }

      productAnalyteMap.set(key, {
        product: productName,
        analyte: result.analyte,
        concentration: displayConcentration,
        loq,
        lod: result.lod,
        unit: displayUnit,
        status,
        colorIntensity
      })
    })

    // Categories that should be rolled up (only show total, not individual analytes)
    const rollupCategories = ['Pesticides', 'Phthalates', 'Mycotoxins']
    
    // Group analytes by category
    const analytesList = Array.from(analytes)
    const analyteGroups: AnalyteGroup[] = []
    const categorizedAnalytes = new Set<string>()
    
    // First, add analytes that match defined categories
    TEST_CATEGORIES.forEach(category => {
      const matchingAnalytes = analytesList.filter(analyte => 
        category.analytes.includes(analyte)
      )
      if (matchingAnalytes.length > 0) {
        // For rollup categories, create synthetic "Total" rows
        if (rollupCategories.includes(category.name)) {
          // Calculate rollup for each product
          Array.from(products).forEach(product => {
            const analytesInCategory = matchingAnalytes
            let sum = 0
            let detectedCount = 0
            let hasAnyBelowLoq = false
            let loqSum = 0  // For pesticides: sum of LOQ values for <LOQ cases
            let maxLoq = 0  // For mycotoxins: track highest LOQ value
            let maxLod = 0  // Track highest LOD value for ND cases
            let maxPercentage = 0  // Track highest percentage of limit from nested cells
            const contributors: Array<{ analyte: string; concentration: number; unit: string }> = []
            let commonUnit = ''
            
            // Determine if this is mycotoxins category
            const isMycotoxins = category.name === 'Mycotoxins'
            
            analytesInCategory.forEach(analyte => {
              const key = `${product}|${analyte}`
              const cell = productAnalyteMap.get(key)
              
              if (cell) {
                // Ensure concentration, loq, and lod are numbers
                const concRaw = cell.concentration
                const loqRaw = cell.loq
                const lodRaw = cell.lod
                
                const conc = concRaw !== null && concRaw !== undefined 
                  ? (typeof concRaw === 'number' ? concRaw : parseFloat(concRaw as any))
                  : null
                  
                const loq = loqRaw !== null && loqRaw !== undefined
                  ? (typeof loqRaw === 'number' ? loqRaw : parseFloat(loqRaw as any))
                  : null
                
                const lod = lodRaw !== null && lodRaw !== undefined
                  ? (typeof lodRaw === 'number' ? lodRaw : parseFloat(lodRaw as any))
                  : null
                
                // Track max LOD across all analytes
                if (lod && !isNaN(lod) && lod > 0) {
                  maxLod = Math.max(maxLod, lod)
                }
                
                // Set common unit from first cell
                if (!commonUnit && cell.unit) {
                  commonUnit = cell.unit
                }
                
                // Check if concentration is quantifiable
                if (conc !== null && !isNaN(conc) && conc > 0) {
                  // For rollup categories: no LOQ requirement, or must be >= LOQ if LOQ exists
                  if (!loq || isNaN(loq) || conc >= loq) {
                    sum += conc
                    detectedCount++
                    contributors.push({ analyte, concentration: conc, unit: cell.unit })
                    
                    // Calculate percentage of limit for this cell
                    const thresholds = selectedGuideline === 'regulatory' ? regulatoryThresholds : otherThresholds
                    const threshold = thresholds?.find(t => t.analyte === analyte)
                    const limitValue = threshold?.converted_value_ug_kg
                    if (limitValue && limitValue > 0) {
                      const percentage = (conc / limitValue) * 100
                      maxPercentage = Math.max(maxPercentage, percentage)
                    }
                  } else if (loq && !isNaN(loq) && conc < loq) {
                    hasAnyBelowLoq = true
                    if (isMycotoxins) {
                      maxLoq = Math.max(maxLoq, loq)
                    } else {
                      loqSum += loq  // Add LOQ value to sum for pesticides
                    }
                  }
                } else if (loq && !isNaN(loq) && loq > 0) {
                  // If no concentration but LOQ exists, count it as <LOQ
                  hasAnyBelowLoq = true
                  if (isMycotoxins) {
                    maxLoq = Math.max(maxLoq, loq)
                  } else {
                    loqSum += loq
                  }
                }
              }
            })
            
            // Sort contributors by concentration (descending) and take top 3
            const topContributors = contributors
              .sort((a, b) => b.concentration - a.concentration)
              .slice(0, 3)
            
            // Create rollup cell
            const rollupAnalyteName = `Total ${category.name}`
            const rollupKey = `${product}|${rollupAnalyteName}`
            
            let status: HeatmapCell['status'] = 'ND'
            let colorIntensity = 0
            
            if (sum > 0) {
              // Use threshold from API if available for total
              const thresholds = selectedGuideline === 'regulatory' ? regulatoryThresholds : otherThresholds
              const threshold = thresholds?.find(t => t.analyte === rollupAnalyteName)
              const benchmark = threshold?.converted_value_ug_kg
              if (benchmark) {
                if (sum < benchmark * 0.5) {
                  status = 'detected'
                  colorIntensity = 0.5
                } else if (sum < benchmark) {
                  status = 'detected'
                  colorIntensity = 0.7
                } else {
                  status = 'high'
                  colorIntensity = 1.0
                }
              } else {
                status = 'detected'
                colorIntensity = 0.6
              }
            } else if (hasAnyBelowLoq) {
              status = 'trace'
              colorIntensity = 0.3
            }
            
            // For mycotoxins, use max LOQ; for pesticides, use sum of LOQs
            const displayLoq = isMycotoxins ? maxLoq : loqSum
            
            productAnalyteMap.set(rollupKey, {
              product,
              analyte: rollupAnalyteName,
              concentration: sum > 0 ? sum : null,
              loq: hasAnyBelowLoq && sum === 0 ? parseFloat(displayLoq.toFixed(3)) : null,  // Store LOQ (max for mycotoxins, sum for pesticides), rounded to 3 decimals
              lod: maxLod > 0 ? parseFloat(maxLod.toFixed(3)) : null,  // Store max LOD across all analytes
              unit: commonUnit,
              status,
              colorIntensity,
              isRollup: true,
              rollupMaxPercentage: maxPercentage > 0 ? maxPercentage : undefined,  // Store max percentage from nested cells
              rollupDetails: {
                detectedCount,
                totalAnalytes: analytesInCategory.length,
                topContributors
              }
            })
          })
          
          // Add rollup analyte and store the individual analytes for expansion
          analyteGroups.push({
            category: category.name,
            analytes: [`Total ${category.name}`],
            individualAnalytes: matchingAnalytes.sort()
          })
        } else {
          // Normal category - show all analytes
          analyteGroups.push({
            category: category.name,
            analytes: matchingAnalytes.sort()
          })
        }
        matchingAnalytes.forEach(a => categorizedAnalytes.add(a))
      }
    })
    
    // Hidden for now: Add uncategorized analytes to "Other"
    // const uncategorized = analytesList.filter(a => !categorizedAnalytes.has(a))
    // if (uncategorized.length > 0) {
    //   analyteGroups.push({
    //     category: 'Other',
    //     analytes: uncategorized.sort()
    //   })
    // }
    
    // Compute maximum LOD per analyte across all products (standardizes detection threshold within each row)
    // Using max LOD ensures the displayed threshold is conservative: any value below it was undetectable
    // by at least one lab in the dataset.
    const minLoqByAnalyte = new Map<string, number>()
    productAnalyteMap.forEach((cell) => {
      if (cell.lod !== null && cell.lod !== undefined) {
        const lodVal = typeof cell.lod === 'number' ? cell.lod : parseFloat(cell.lod as any)
        if (!isNaN(lodVal)) {
          const current = minLoqByAnalyte.get(cell.analyte)
          if (current === undefined || lodVal > current) {
            minLoqByAnalyte.set(cell.analyte, lodVal)
          }
        }
      }
    })

    // Calculate percentile-based coloring for Other Standards
    // Do this AFTER rollup creation so rollup totals are included
    if (selectedGuideline === 'other') {
      // Collect all rollup total values as well
      productAnalyteMap.forEach((cell, key) => {
        if (cell.isRollup && cell.concentration !== null && cell.concentration > 0) {
          if (!analyteValues.has(cell.analyte)) {
            analyteValues.set(cell.analyte, [])
          }
          analyteValues.get(cell.analyte)!.push(cell.concentration)
        }
      })
      
      analyteValues.forEach((values, analyteName) => {
        if (values.length === 0) return
        
        // Sort values to calculate percentiles
        const sortedValues = [...values].sort((a, b) => a - b)
        const p50 = sortedValues[Math.floor(sortedValues.length * 0.50)]
        const p75 = sortedValues[Math.floor(sortedValues.length * 0.75)]
        
        // Update cells with percentile-based coloring
        // ONLY for cells with actual detected values (not <LOQ)
        productAnalyteMap.forEach((cell, key) => {
          // Skip if not the right analyte
          if (cell.analyte !== analyteName) return
          
          // Skip if <LOQ (concentration is null, 0, or below standardized min LOQ for this analyte)
          const effectiveLoq = minLoqByAnalyte.get(cell.analyte) ?? cell.loq
          const hasActualValue = cell.concentration !== null &&
                                 cell.concentration !== undefined &&
                                 cell.concentration > 0 &&
                                 (!effectiveLoq || cell.concentration >= effectiveLoq)
          
          if (!hasActualValue) {
            // <LOQ values: keep initial status (ND/trace) - will appear gray
            return
          }
          
          const conc = cell.concentration!
          
          // For contaminants (lower is better)
          if (conc <= p50) {
            cell.status = 'trace' // 0-50% - yellow
            cell.colorIntensity = 0.3
          } else if (conc <= p75) {
            cell.status = 'detected' // 50-75% - orange
            cell.colorIntensity = 0.6
          } else {
            cell.status = 'high' // 75-100% - red
            cell.colorIntensity = 1.0
          }
        })
      })
    }

    let sortedProducts = Array.from(products).sort()
    
    // Apply sorting if active
    if (sortByAnalyte && sortDirection) {
      sortedProducts = sortedProducts.sort((a, b) => {
        const cellA = productAnalyteMap.get(`${a}|${sortByAnalyte}`)
        const cellB = productAnalyteMap.get(`${b}|${sortByAnalyte}`)
        
        // Determine sort values
        // If concentration is null/0 (below LOQ), use LOQ but mark it as "below detection"
        const getComparisonValue = (cell: HeatmapCell | undefined): { value: number, isBelowLOQ: boolean } => {
          if (!cell) return { value: -Infinity, isBelowLOQ: true }
          
          const hasActualValue = cell.concentration !== null && cell.concentration !== undefined && cell.concentration > 0
          
          if (hasActualValue) {
            return { value: cell.concentration!, isBelowLOQ: false }
          } else {
            // Below LOQ - use LOQ value but mark it
            const loqValue = cell.loq ?? 0
            return { value: loqValue, isBelowLOQ: true }
          }
        }
        
        const compA = getComparisonValue(cellA)
        const compB = getComparisonValue(cellB)
        
        // Sort logic: actual values always rank higher than LOQ values
        if (sortDirection === 'desc') {
          // Descending: highest to lowest
          // Actual values come before LOQ values
          if (compA.isBelowLOQ && !compB.isBelowLOQ) return 1 // B (actual) comes first
          if (!compA.isBelowLOQ && compB.isBelowLOQ) return -1 // A (actual) comes first
          // Both same type, compare values
          return compB.value - compA.value
        } else {
          // Ascending: lowest to highest
          // LOQ values come before actual values
          if (compA.isBelowLOQ && !compB.isBelowLOQ) return -1 // A (LOQ) comes first
          if (!compA.isBelowLOQ && compB.isBelowLOQ) return 1 // B (LOQ) comes first
          // Both same type, compare values
          return compA.value - compB.value
        }
      })
    }
    
    return {
      products: sortedProducts,
      analytes: Array.from(analytes).sort(),
      analyteGroups,
      cells: productAnalyteMap,
      minLoqByAnalyte
    }
  }, [data, selectedGuideline, formulaTargets, sortByAnalyte, sortDirection, regulatoryThresholds, otherThresholds])
  
  // Compute expanded analyte groups based on expandedRollups state
  const displayedAnalyteGroups = useMemo(() => {
    return heatmapData.analyteGroups.flatMap(group => {
      if (group.individualAnalytes && expandedRollups.has(group.category)) {
        // Expanded: show Total row followed by individual analytes
        return [
          { ...group }, // Total row
          { 
            category: group.category, 
            analytes: group.individualAnalytes,
            isExpanded: true 
          }
        ]
      }
      return [group]
    })
  }, [heatmapData.analyteGroups, expandedRollups])
  
  const toggleRollupExpansion = (category: string) => {
    setExpandedRollups(prev => {
      const newSet = new Set(prev)
      if (newSet.has(category)) {
        newSet.delete(category)
      } else {
        newSet.add(category)
      }
      return newSet
    })
  }

  const handleAnalyteSort = (analyte: string) => {
    if (sortByAnalyte === analyte) {
      // Cycle through: desc -> asc -> null
      if (sortDirection === 'desc') {
        setSortDirection('asc')
      } else if (sortDirection === 'asc') {
        setSortByAnalyte(null)
        setSortDirection(null)
      }
    } else {
      // Start with descending (highest first)
      setSortByAnalyte(analyte)
      setSortDirection('desc')
    }
  }
  
  const getSortIcon = (analyte: string, isVertical: boolean = false) => {
    if (sortByAnalyte !== analyte) {
      return <ArrowUpDown className={cn("h-3 w-3 text-gray-400", isVertical && "rotate-90")} />
    }
    
    // In vertical view, products go left to right, so arrows are reversed
    if (isVertical) {
      if (sortDirection === 'desc') {
        // Descending: highest to lowest → highest on LEFT (arrow points left)
        return <ArrowUp className="h-3 w-3 text-blue-600 rotate-90" />
      }
      // Ascending: lowest to highest → highest on RIGHT (arrow points right)
      return <ArrowDown className="h-3 w-3 text-blue-600 rotate-90" />
    }
    
    // In horizontal view, products go top to bottom
    if (sortDirection === 'desc') {
      // Descending: highest to lowest → highest on TOP (arrow points down)
      return <ArrowDown className="h-3 w-3 text-blue-600" />
    }
    // Ascending: lowest to highest → highest on BOTTOM (arrow points up)
    return <ArrowUp className="h-3 w-3 text-blue-600" />
  }

  const hasRegulatoryLimit = (analyteName: string) => {
    // Check if threshold exists in API data
    const thresholds = selectedGuideline === 'regulatory' ? regulatoryThresholds : otherThresholds
    return thresholds?.some(t => t.analyte === analyteName) ?? false
  }

  const getCellColor = (cell: HeatmapCell | undefined) => {
    if (!cell) return 'bg-gray-100'
    
    // Check if this is a nutrient (positive scale category)
    const category = TEST_CATEGORIES.find(cat => cat.analytes.includes(cell.analyte))
    const isNutrient = category?.positiveScale !== undefined
    
    // Skip coloring for nutrients - show gray
    if (isNutrient) {
      return 'bg-gray-200'
    }
    
    // For rollup cells, use the max percentage from nested cells if available
    if (cell.isRollup && cell.rollupMaxPercentage) {
      const percentage = cell.rollupMaxPercentage
      if (percentage >= 1000) {
        return 'bg-[#6292F9]'  // ≥1000%
      } else if (percentage >= 500) {
        return 'bg-[#6292F9]'  // ≥500%
      } else if (percentage >= 100) {
        return 'bg-[#6292F9]'  // ≥100%
      } else if (percentage > 20) {
        return 'bg-[#7AA3FA]'  // >20%
      } else {
        return 'bg-[#9DBBFB]'  // ≤20%
      }
    }

    // Check if cell has actual quantifiable value
    const conc = typeof cell.concentration === 'number'
      ? cell.concentration
      : (cell.concentration ? parseFloat(cell.concentration as any) : null)
    const cellLod = typeof cell.lod === 'number'
      ? cell.lod
      : (cell.lod ? parseFloat(cell.lod as any) : null)
    const cellLoqFallback = typeof cell.loq === 'number'
      ? cell.loq
      : (cell.loq ? parseFloat(cell.loq as any) : null)
    const loq = heatmapData.minLoqByAnalyte.get(cell.analyte) ?? cellLod ?? cellLoqFallback

    const hasActualValue = conc !== null &&
                           !isNaN(conc) &&
                           conc > 0 &&
                           (!loq || isNaN(loq) || conc >= loq)
    
    // Check if analyte has a guideline threshold
    const hasLimit = hasRegulatoryLimit(cell.analyte)
    
    // If no limit is defined
    if (!hasLimit) {
      // If detected but no guideline, use very light blue
      if (hasActualValue) {
        return 'bg-[#C2D5FD]'
      }
      // If no actual value (ND or <LOQ), show gray
      return 'bg-gray-200'
    }
    
    // If no actual value (ND or <LOQ), show gray
    if (!hasActualValue) {
      return 'bg-gray-200'
    }
    
    // Has actual value and guideline threshold - calculate percentage and color accordingly
    const percentage = calculatePercentageOfLimit(cell)
    
    if (percentage !== null) {
      if (percentage >= 1000) {
        return 'bg-[#6292F9]'  // ≥1000%
      } else if (percentage >= 500) {
        return 'bg-[#6292F9]'  // ≥500%
      } else if (percentage >= 100) {
        return 'bg-[#6292F9]'  // ≥100%
      } else if (percentage > 20) {
        return 'bg-[#7AA3FA]'  // >20%
      } else {
        return 'bg-[#9DBBFB]'  // ≤20%
      }
    }

    return 'bg-gray-200'
  }
  
  const getCellBackgroundPattern = (cell: HeatmapCell | undefined) => {
    if (!cell) return ''
    
    // In percentile mode, check if cell has actual value
    if (selectedGuideline === 'other') {
      const hasActualValue = cell.concentration !== null && 
                             cell.concentration !== undefined && 
                             cell.concentration > 0 &&
                             (!cell.loq || cell.concentration >= cell.loq)
      
      // Only apply pattern to <LOQ values
      if (!hasActualValue) {
        if (noLimitStyle === 'striped') {
          return 'bg-stripe-pattern'
        } else if (noLimitStyle === 'dotted') {
          return 'bg-dot-pattern'
        }
      }
      return ''
    }
    
    // For regulatory guidelines, check if analyte has a limit
    const hasLimit = hasRegulatoryLimit(cell.analyte)
    if (hasLimit) return ''
    
    if (noLimitStyle === 'striped') {
      return 'bg-stripe-pattern'
    } else if (noLimitStyle === 'dotted') {
      return 'bg-dot-pattern'
    }
    
    return ''
  }

  const getRegulatoryLimit = (analyteName: string): number | null => {
    // Use the appropriate threshold based on selected guideline
    const thresholds = selectedGuideline === 'regulatory' ? regulatoryThresholds : otherThresholds
    const threshold = thresholds?.find(t => t.analyte === analyteName)
    const value = threshold?.converted_value_ug_kg ?? null
    
    if (analyteName === 'Arsenic') {
      console.log(`Getting limit for Arsenic in ${selectedGuideline} view:`, {
        thresholdsCount: thresholds?.length,
        threshold,
        value,
        allThresholds: thresholds?.map(t => ({ analyte: t.analyte, value: t.converted_value_ug_kg }))
      })
    }
    
    return value
  }

  const formatValue = (cell: HeatmapCell | undefined, includeUnit: boolean = false) => {
    if (!cell) return 'ND'
    
    // Check if analyte belongs to a category that doesn't show ERROR (no LOQ expected)
    const noErrorCategories = ['Macronutrients', 'Pesticides', 'Phthalates', 'Mycotoxins']
    const category = TEST_CATEGORIES.find(cat => cat.analytes.includes(cell.analyte))
    const isNoErrorCategory = (category && noErrorCategories.includes(category.name)) || 
                              cell.analyte.startsWith('Total ')
    
    const isPesticide = (category?.name === 'Pesticides') || cell.analyte.startsWith('Total Pesticides')
    const isMycotoxin = (category?.name === 'Mycotoxins') || cell.analyte.startsWith('Total Mycotoxins')

    // Standardized detection threshold: use maximum LOD for this analyte across all products.
    // Falls back to cell's own LOD, then cell's own LOQ (for rollup/mycotoxin cells where LOD is absent).
    const cellLodRaw = typeof cell.lod === 'number' ? cell.lod : (cell.lod ? parseFloat(cell.lod as any) : null)
    const cellLoqRaw = typeof cell.loq === 'number' ? cell.loq : (cell.loq ? parseFloat(cell.loq as any) : null)
    const effectiveLoq = heatmapData.minLoqByAnalyte.get(cell.analyte) ?? cellLodRaw ?? cellLoqRaw

    // Handle ND or null concentration
    if (cell.concentration === null || cell.concentration === undefined) {
      // For rollup cells showing <LOQ
      if (cell.isRollup && cell.status === 'trace') {
        if ((isPesticide || isMycotoxin) && effectiveLoq !== null) {
          return `<${formatNumber(effectiveLoq)}`
        }
        return '<LOQ'
      }

      // Show <effectiveLoq if available
      if (effectiveLoq !== null) {
        return `<${formatNumber(effectiveLoq)}`
      }

      // Fall back to LOD for ND cells with no LOQ data
      if (cell.lod !== null && cell.lod !== undefined) {
        const lodValue = typeof cell.lod === 'number' ? cell.lod : parseFloat(cell.lod as any)
        return `<${formatNumber(lodValue)}`
      }

      if (isNoErrorCategory) {
        return 'ND'
      }

      return 'ERROR'
    }

    // Ensure concentration is a number
    const conc = typeof cell.concentration === 'number' ? cell.concentration : parseFloat(cell.concentration as any)

    // Handle NaN or 0 concentration (also ND)
    if (isNaN(conc) || conc === 0) {
      if (effectiveLoq !== null) {
        return `<${formatNumber(effectiveLoq)}`
      }

      if (cell.lod !== null && cell.lod !== undefined) {
        const lodValue = typeof cell.lod === 'number' ? cell.lod : parseFloat(cell.lod as any)
        return `<${formatNumber(lodValue)}`
      }

      if (isNoErrorCategory) {
        return 'ND'
      }

      return 'ERROR'
    }

    // Handle below standardized LOQ — skip for rollup cells, whose concentration is a real
    // detected sum (not subject to individual-analyte detection thresholds)
    if (!cell.isRollup && effectiveLoq !== null && conc < effectiveLoq) {
      return `<${formatNumber(effectiveLoq)}`
    }
    
    const formattedValue = formatNumber(conc)
    
    // Add unit and normalization suffix if requested
    if (includeUnit) {
      const suffix = getNormalizedSuffix(cell.analyte)
      return `${formattedValue} ${cell.unit}${suffix}`
    }
    
    return formattedValue
  }
  
  // Smart rounding: 1 decimal if >= 1, 3 decimals if < 1
  const formatNumber = (value: number): string => {
    if (value >= 1) {
      return value.toFixed(1)
    } else {
      return value.toFixed(3)
    }
  }
  
  // Calculate percentage of limit (regulatory or other)
  const calculatePercentageOfLimit = (cell: HeatmapCell | undefined): number | null => {
    if (!cell || cell.concentration === null || cell.concentration === undefined) return null
    
    const conc = typeof cell.concentration === 'number' ? cell.concentration : parseFloat(cell.concentration as any)
    if (isNaN(conc) || conc === 0) return null
    
    // Skip if below LOQ
    if (cell.loq && conc < cell.loq) return null
    
    // Get limit from API (works for both regulatory and other)
    const limitValue = getRegulatoryLimit(cell.analyte)
    
    if (limitValue !== null && limitValue > 0) {
      return (conc / limitValue) * 100
    }

    if (limitValue === 0) {
      return Infinity
    }

    return null
  }

  if (heatmapData.products.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No data available</p>
      </div>
    )
  }

  return (
    <div className="w-full">

      {/* Horizontal View: Products as rows, Analytes as columns */}
      {orientation === 'horizontal' && (
        <div className="overflow-auto border rounded-md shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              {/* Category header row */}
              <tr className="h-[40px]">
                <th className="px-4 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 border-b-2 border-gray-300 h-[40px] min-w-[300px] w-[300px] cursor-default select-none" rowSpan={2}>
                  Product
                </th>
                {heatmapData.analyteGroups.map(group => (
                  <th
                    key={group.category}
                    colSpan={group.analytes.length}
                    className="px-2 py-2 text-center text-sm font-medium text-gray-900 border-b-2 border-gray-300 bg-gray-100 h-[40px] cursor-default select-none"
                  >
                    {group.category}
                  </th>
                ))}
              </tr>
              {/* Analyte header row */}
              <tr className="h-[40px]">
                {heatmapData.analyteGroups.map(group =>
                  group.analytes.map(analyte => (
                    <th
                      key={analyte}
                      className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[132px] w-[132px] border-l border-gray-200 h-[40px] cursor-pointer hover:bg-gray-100 select-none"
                      title={analyte}
                      onClick={() => handleAnalyteSort(analyte)}
                    >
                      <div className="flex items-center justify-center gap-1">
                        <span className="truncate">{getAnalyteFriendlyName(analyte)}</span>
                        {getAnalyteMetadata(analyte) && (
                          <Info 
                            className="h-3 w-3 text-gray-400 hover:text-blue-600 flex-shrink-0"
                            onMouseEnter={(e) => {
                              e.stopPropagation()
                              const rect = e.currentTarget.getBoundingClientRect()
                              setHoveredAnalyteInfo({ analyte, position: { x: rect.left, xRight: window.innerWidth - rect.right, y: rect.bottom + 5, yAbove: rect.top } })
                            }}
                            onMouseLeave={(e) => {
                              e.stopPropagation()
                              setHoveredAnalyteInfo(null)
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <span className="flex-shrink-0">{getSortIcon(analyte)}</span>
                      </div>
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {heatmapData.products.map(product => (
                <tr key={product} className="h-[40px]">
                  <td className="px-4 py-2 text-sm font-medium text-gray-900 sticky left-0 bg-white z-10 h-[40px] min-w-[300px] w-[300px] truncate cursor-default select-none" title={product}>
                    {product}
                  </td>
                  {heatmapData.analyteGroups.map(group =>
                    group.analytes.map(analyte => {
                      const key = `${product}|${analyte}`
                      const cell = heatmapData.cells.get(key)
                      const value = formatValue(cell)
                      const showIcon = noLimitStyle === 'icon'
                      return (
                        <td
                          key={key}
                          className={cn(
                            "px-2 py-2 text-xs text-center cursor-pointer transition-all hover:ring-2 hover:ring-blue-500 border-l border-gray-200 min-w-[132px] w-[132px] h-[40px] relative",
                            value === 'ERROR' ? 'bg-red-500 text-white font-bold' : getCellColor(cell),
                            getCellBackgroundPattern(cell)
                          )}
                          onClick={() => setSelectedCell(cell || null)}
                          title={''}
                        >
                          <div className="flex items-center justify-center gap-1">
                            {value}
                            {showIcon && (
                              <Info 
                                className="h-3 w-3 text-gray-500 cursor-pointer" 
                                onMouseEnter={(e) => {
                                  if (hoverTimeoutRef.current) {
                                    clearTimeout(hoverTimeoutRef.current)
                                    hoverTimeoutRef.current = null
                                  }
                                  setHoveredCell(cell || null)
                                  const rect = e.currentTarget.getBoundingClientRect()
                                  setHoverPosition({ x: rect.left, xRight: window.innerWidth - rect.right, y: rect.bottom + 5 })
                                }}
                                onMouseLeave={() => {
                                  hoverTimeoutRef.current = setTimeout(() => {
                                    setHoveredCell(null)
                                    setHoverPosition(null)
                                  }, 200)
                                }}
                              />
                            )}
                          </div>
                        </td>
                      )
                    })
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Color Key */}
      <div className="flex flex-wrap items-center justify-between gap-4 px-1 pt-2 pb-4 text-xs text-gray-600">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Guideline:</span>
          <select
            value={selectedGuideline}
            onChange={(e) => setSelectedGuideline(e.target.value)}
            className="text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200 rounded-md shadow-sm pl-2 pr-6 py-1 bg-gray-50 focus:outline-none appearance-none cursor-pointer"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%234b5563' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 6px center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '12px 12px',
            }}
          >
            <option value="regulatory">Regulatory Standards</option>
            <option value="other">Health Safety Limit</option>
          </select>
          {selectedGuideline === 'other' && (
            <Info
              className="h-3 w-3 text-gray-400 hover:text-blue-600 flex-shrink-0 cursor-pointer"
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect()
                setHoveredGuidelineInfo({ position: { x: rect.left, xRight: window.innerWidth - rect.right, y: rect.bottom + 5, yAbove: rect.top } })
              }}
              onMouseLeave={() => setHoveredGuidelineInfo(null)}
            />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-sm bg-gray-200 border border-gray-300" />
          <span>Not Detected</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-sm bg-[#C2D5FD] border border-gray-300" />
          <span>Detected (No Guideline)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-sm bg-[#9DBBFB] border border-gray-300" />
          <span>≤20% of Limit</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-sm bg-[#7AA3FA] border border-gray-300" />
          <span>&gt;20% of Limit</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-sm bg-[#6292F9] border border-gray-300" />
          <span>≥100% of Limit</span>
        </div>
        </div>
      </div>

      {/* Vertical View: Analytes as rows, Products as columns */}
      {orientation === 'vertical' && (
        <div className="overflow-auto border rounded-md shadow-sm">
          <table className="min-w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed' }}>
            <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm">
              <tr className="h-[40px]">
                <th 
                  colSpan={2}
                  className="px-2 py-1 text-center text-xs font-bold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-30 h-[40px] min-w-[200px] w-[200px] cursor-default select-none border-r border-gray-200"
                />
                {heatmapData.products.map(product => (
                  <th
                    key={product}
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[160px] w-[160px] max-w-[160px] border-l border-gray-200 h-[40px] bg-gray-50 cursor-default select-none"
                    title={getProductFriendlyName(product)}
                  >
                    <div className="truncate">
                      {getProductFriendlyName(product)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayedAnalyteGroups.map((group, groupIdx) =>
                group.analytes.map((analyte, idx) => {
                  const isRollupRow = analyte.startsWith('Total ')
                  const hasIndividualAnalytes = group.individualAnalytes && group.individualAnalytes.length > 0
                  const isExpanded = expandedRollups.has(group.category)
                  
                  return (
                  <tr key={`${group.category}-${analyte}`} className="h-[40px]">
                    {idx === 0 && (
                      <td 
                        rowSpan={group.analytes.length}
                        className="px-1 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky left-0 z-10 border-r-2 border-gray-300 align-middle min-w-[50px] w-[50px] cursor-default select-none"
                        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                      >
                        <div className="flex items-center justify-center h-full">
                          {group.category}
                        </div>
                      </td>
                    )}
                    <td
                      className={`px-4 py-2 text-sm font-medium text-gray-900 sticky left-[50px] bg-white z-10 h-[40px] min-w-[150px] w-[150px] max-w-[150px] select-none ${isRollupRow && hasIndividualAnalytes ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'}`}
                      title={analyte}
                      onClick={isRollupRow && hasIndividualAnalytes ? () => toggleRollupExpansion(group.category) : undefined}
                    >
                      <div className="flex items-center gap-1">
                        {isRollupRow && hasIndividualAnalytes && (
                          <span className="flex-shrink-0">
                            {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          </span>
                        )}
                        <span className="truncate">{getAnalyteFriendlyName(analyte)}</span>
                        {getAnalyteMetadata(analyte) && (
                          <Info
                            className="h-3 w-3 text-gray-400 hover:text-blue-600 flex-shrink-0"
                            onMouseEnter={(e) => {
                              e.stopPropagation()
                              const rect = e.currentTarget.getBoundingClientRect()
                              setHoveredAnalyteInfo({ analyte, position: { x: rect.left, xRight: window.innerWidth - rect.right, y: rect.bottom + 5, yAbove: rect.top } })
                            }}
                            onMouseLeave={(e) => {
                              e.stopPropagation()
                              setHoveredAnalyteInfo(null)
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <span
                          className="flex-shrink-0 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleAnalyteSort(analyte)
                          }}
                        >
                          {getSortIcon(analyte, true)}
                        </span>
                      </div>
                    </td>
                    {heatmapData.products.map(product => {
                      const key = `${product}|${analyte}`
                      const cell = heatmapData.cells.get(key)
                      
                      const value = formatValue(cell)
                      const showIcon = noLimitStyle === 'icon'
                      return (
                        <td
                          key={key}
                          className={cn(
                            "px-2 py-2 text-xs text-center cursor-pointer transition-all hover:ring-2 hover:ring-blue-500 border-l border-gray-200 min-w-[160px] w-[160px] max-w-[160px] h-[40px] relative",
                            value === 'ERROR' ? 'bg-red-500 text-white font-bold' : getCellColor(cell),
                            getCellBackgroundPattern(cell)
                          )}
                          onClick={() => setSelectedCell(cell || null)}
                          title={''}
                        >
                          <div className="flex items-center justify-center gap-1">
                            {value}
                            {showIcon && (
                              <Info 
                                className="h-3 w-3 text-gray-500 cursor-pointer" 
                                onMouseEnter={(e) => {
                                  if (hoverTimeoutRef.current) {
                                    clearTimeout(hoverTimeoutRef.current)
                                    hoverTimeoutRef.current = null
                                  }
                                  setHoveredCell(cell || null)
                                  const rect = e.currentTarget.getBoundingClientRect()
                                  setHoverPosition({ x: rect.left, xRight: window.innerWidth - rect.right, y: rect.bottom + 5 })
                                }}
                                onMouseLeave={() => {
                                  hoverTimeoutRef.current = setTimeout(() => {
                                    setHoveredCell(null)
                                    setHoverPosition(null)
                                  }, 200)
                                }}
                              />
                            )}
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Floating Analyte Info on Hover */}
      {hoveredAnalyteInfo && (() => {
        const metadata = getAnalyteMetadata(hoveredAnalyteInfo.analyte)
        if (!metadata) return null
        
        return (
          <div 
            className="fixed z-50 p-4 bg-white border-2 border-blue-300 rounded-lg shadow-xl max-w-md"
            style={{
              ...(hoveredAnalyteInfo.position.x + 448 <= window.innerWidth
                ? { left: `${hoveredAnalyteInfo.position.x}px` }
                : { right: `${hoveredAnalyteInfo.position.xRight}px` }),
              ...(hoveredAnalyteInfo.position.y + 400 <= window.innerHeight
                ? { top: `${hoveredAnalyteInfo.position.y}px` }
                : { bottom: `${window.innerHeight - hoveredAnalyteInfo.position.yAbove + 5}px` }),
              pointerEvents: 'none'
            }}
          >
            <h3 className="text-sm font-bold mb-2 text-blue-900">
              {metadata.analyteName}
              {metadata.shortName && (
                <span className="text-xs font-normal text-gray-600 ml-2">({metadata.shortName})</span>
              )}
            </h3>
            
            <div className="space-y-3 text-xs">
              <div>
                <p className="text-gray-600">{metadata.description}</p>
              </div>
              
              <div>
                <p className="font-semibold text-gray-700 mb-1">Health Effects</p>
                <p className="text-gray-600">{metadata.healthEffects}</p>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Guideline Info Tooltip */}
      {hoveredGuidelineInfo && (
        <div
          className="fixed z-50 p-4 bg-card border border-border rounded-md shadow-lg max-w-sm"
          style={{
            ...(hoveredGuidelineInfo.position.x + 384 <= window.innerWidth
              ? { left: `${hoveredGuidelineInfo.position.x}px` }
              : { right: `${hoveredGuidelineInfo.position.xRight}px` }),
            ...(hoveredGuidelineInfo.position.y + 300 <= window.innerHeight
              ? { top: `${hoveredGuidelineInfo.position.y}px` }
              : { bottom: `${window.innerHeight - hoveredGuidelineInfo.position.yAbove + 5}px` }),
            pointerEvents: 'none'
          }}
        >
          <h3 className="text-sm font-semibold mb-2 border-b border-border pb-2 text-foreground">Health Safety Limit</h3>
          <p className="text-xs text-muted-foreground">Based on EWG's health-protective drinking water guideline and EU guidelines, the most stringent available standard for this chemical.</p>
        </div>
      )}

      {/* Floating Cell Details on Hover */}
      {hoveredCell && hoverPosition && noLimitStyle === 'icon' && (() => {
        const limitValue = getRegulatoryLimit(hoveredCell.analyte)
        
        return (
          <div 
            className="fixed z-50 p-4 bg-card border border-border rounded-md shadow-lg max-w-md"
            style={{
              ...(hoverPosition.x + 448 <= window.innerWidth
                ? { left: `${hoverPosition.x}px` }
                : { right: `${hoverPosition.xRight}px` }),
              top: `${hoverPosition.y}px`,
              pointerEvents: 'auto'
            }}
            onMouseEnter={() => {
              // Keep the popup visible when hovering over it
              if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current)
                hoverTimeoutRef.current = null
              }
            }}
            onMouseLeave={() => {
              // Clear the hover state when leaving the popup
              hoverTimeoutRef.current = setTimeout(() => {
                setHoveredCell(null)
                setHoverPosition(null)
              }, 200)
            }}
          >
            <h3 className="text-sm font-semibold mb-3 border-b border-border pb-2 text-foreground">
              {getProductFriendlyName(hoveredCell.product)}
            </h3>
            
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-muted-foreground">Analyte</p>
                <p className="font-medium text-foreground">{hoveredCell.analyte}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Concentration</p>
                <p className="font-medium text-foreground">
                  {hoveredCell.concentration !== null && hoveredCell.concentration !== undefined
                    ? (() => {
                        const conc = typeof hoveredCell.concentration === 'number' 
                          ? hoveredCell.concentration 
                          : parseFloat(hoveredCell.concentration as any)
                        const suffix = getNormalizedSuffix(hoveredCell.analyte)
                        return !isNaN(conc) 
                          ? `${formatNumber(conc)} ${hoveredCell.unit}${suffix}`
                          : `${hoveredCell.concentration} ${hoveredCell.unit}${suffix}`
                      })()
                    : 'Not Detected'}
                </p>
              </div>
              {shouldNormalizeAnalyte(hoveredCell.analyte) && (<div>
                <p className="text-muted-foreground">Label Claim</p>
                <p className="font-medium text-foreground">
                  {shouldNormalizeAnalyte(hoveredCell.analyte) ? (() => {
                    // Show NFP label claim for normalized analytes
                    const nutrientData = getFormulaNutrient(hoveredCell.product, hoveredCell.analyte)
                    if (nutrientData && nutrientData.value) {
                      return `${nutrientData.value} ${nutrientData.unit}/serving (per 100 cal)`
                    }
                    return 'N/A'
                  })() : (
                    'N/A'
                  )}
                </p>
              </div>)}
              {/* <div>
                <p className="text-gray-600">LOQ</p>
                <p className="font-medium">
                  {hoveredCell.loq !== null && hoveredCell.loq !== undefined
                      ? (() => {
                          const loqVal = typeof hoveredCell.loq === 'number'
                            ? hoveredCell.loq
                            : parseFloat(hoveredCell.loq as any)
                          return !isNaN(loqVal)
                            ? `${loqVal} ${hoveredCell.unit}`
                            : `${hoveredCell.loq} ${hoveredCell.unit}`
                        })()
                      : 'N/A'}
                </p>
              </div> */}
              {!shouldNormalizeAnalyte(hoveredCell.analyte) && (() => {
                // Get the threshold object to show guideline information
                const thresholds = selectedGuideline === 'regulatory' ? regulatoryThresholds : otherThresholds
                const threshold = thresholds?.find(t => t.analyte === hoveredCell.analyte)
                const percentage = calculatePercentageOfLimit(hoveredCell)
                
                return threshold && (
                  <div className="col-span-2 pt-2 border-t border-border">
                    <div className="mb-2">
                      <p className="text-xs text-muted-foreground mb-1">Guideline</p>
                      {threshold.reference_url ? (
                        <a 
                          href={threshold.reference_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium text-primary hover:underline"
                        >
                          {threshold.guideline_name}
                        </a>
                      ) : (
                        <span className="text-xs font-medium text-foreground">
                          {threshold.guideline_name}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      {limitValue !== null && (
                        <div>
                          <p className="text-muted-foreground">Limit</p>
                          <p className="font-medium text-foreground">{`${limitValue} ${hoveredCell.unit}`}</p>
                        </div>
                      )}
                      {percentage !== null && (
                        <div>
                          <p className="text-muted-foreground">Limit Ratio</p>
                          <p className="font-medium text-foreground">{percentage === Infinity ? '∞' : `${(percentage / 100).toFixed(2)}x`}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })()}
              {/* Rollup Top Contributors - moved to bottom */}
              {hoveredCell.isRollup && hoveredCell.rollupDetails && hoveredCell.rollupDetails.topContributors.length > 0 && (
                <div className="col-span-2 pt-2 border-t border-border">
                  <p className="text-xs font-medium text-foreground mb-1">Top contributors:</p>
                  <ul className="text-xs space-y-0.5">
                    {hoveredCell.rollupDetails.topContributors.map((c, idx) => {
                      return (
                        <li key={idx} className="text-muted-foreground">
                          • {c.analyte}
                        </li>
                      )
                    })}
                  </ul>
                  {hoveredCell.rollupDetails.detectedCount - hoveredCell.rollupDetails.topContributors.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      +{hoveredCell.rollupDetails.detectedCount - hoveredCell.rollupDetails.topContributors.length} more
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      })()}
    </div>
  )
}
